<template>
  <div
    class="overscroll-x-contain overflow-x-auto bg-surface-default-mid py-16"
  >
    <ul
      class="flex w-max flex-nowrap items-start gap-8 px-24 md:gap-16 lg:px-[max(2rem,calc((100vw-1184px)/2+2rem))]"
    >
      <li
        v-for="criterion in criteria"
        :key="criterion.criterion"
        class="w-[288px] h-[320px] shrink-0"
      >
        <FinderScreensizeTile
          v-if="criterion.criterion === 'screensize' && screensizeCriterion"
          v-model:selected-option-ids="selectedScreensizeOptionIds"
          :criterion="screensizeCriterion"
          @select-range="handleSelectScreensizeRange"
        />
        <FinderUsagesTile
          v-else-if="criterion.criterion === 'usages' && usagesCriterion"
          v-model:selected-ids="selectedUsagesIds"
          :criterion="usagesCriterion"
          :options="usagesOptions"
        />
        <FinderPriceTile
          v-else-if="criterion.criterion === 'budget' && budgetCriterion"
          v-model="price"
          :criterion="budgetCriterion"
          :price-facet
        />
        <FinderConditionsTile
          v-else-if="
            criterion.criterion === 'conditions' && conditionsCriterion
          "
          v-model="selectedConditionThreshold"
          :criterion="conditionsCriterion"
        />
        <FinderRequirementsTile
          v-else-if="
            criterion.criterion === 'requirements' && requirementsCriterion
          "
          v-model:filters="filters"
          :criterion="requirementsCriterion"
          :facets="remainingFacets"
        />
      </li>
    </ul>

    <PlpContainer v-if="activeFiltersCount > 0" class="mt-16">
      <FinderJumpToResults class="mb-16" to-anchor-id="results" />
    </PlpContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type {
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import FinderJumpToResults from '../../components/Finder/components/FinderJumpToResults/FinderJumpToResults.vue'
import PlpContainer from '../../components/PlpContainer/PlpContainer.vue'
import { findCriterion, useFinder } from '../../composables/useFinder/useFinder'
import { getMacbookFinderConfig } from '../../composables/useFinder/useFinder.config.macbook'

import FinderConditionsTile from './components/FinderConditionsTile/FinderConditionsTile.vue'
import FinderPriceTile from './components/FinderPriceTile/FinderPriceTile.vue'
import FinderRequirementsTile from './components/FinderRequirementsTile/FinderRequirementsTile.vue'
import FinderScreensizeTile from './components/FinderScreensizeTile/FinderScreensizeTile.vue'
import FinderUsagesTile from './components/FinderUsagesTile/FinderUsagesTile.vue'

const props = defineProps<{
  facets: Facet[]
  priceFacet?: UiPriceFacet
}>()

const filters = defineModel<Record<string, string[]>>('filters', {
  default: () => ({}),
})
const price = defineModel<[number, number] | undefined>('price')

const i18n = useI18n()

const config = getMacbookFinderConfig(i18n)
const screensizeCriterion = findCriterion(config, 'screensize')
const usagesCriterion = findCriterion(config, 'usages')
const budgetCriterion = findCriterion(config, 'budget')
const conditionsCriterion = findCriterion(config, 'conditions')
const requirementsCriterion = findCriterion(config, 'requirements')

const configuredFacetNames = new Set(
  config.criteria.flatMap((criterion) =>
    criterion.kind === 'budget' || criterion.kind === 'requirements'
      ? []
      : [criterion.facetName],
  ),
)

const remainingFacets = computed(() =>
  props.facets.filter((facet) => !configuredFacetNames.has(facet.name)),
)

const criteria = config.criteria.map((c) => ({
  criterion: c.kind,
}))

const finder = useFinder(config, filters.value, price)

const USAGE_FACET_NAME = 'usages.label'

const usageFacet = computed(() =>
  props.facets.find((f) => f.name === USAGE_FACET_NAME),
)

const usagesOptions = computed(() => {
  const facet = usageFacet.value
  if (!usagesCriterion || !facet) return []

  return usagesCriterion.options
    .map((option) => {
      const facetValue = facet.values.find((v) => v.name === option.values[0])

      return facetValue
        ? {
            id: option.id,
            label: facetValue.label,
            values: option.values,
          }
        : null
    })
    .filter((o): o is NonNullable<typeof o> => o !== null)
})

const selectedScreensizeOptionIds = computed<string[]>({
  get() {
    if (!screensizeCriterion) return []

    return screensizeCriterion.options
      .filter((option) =>
        finder.isOptionSelected(screensizeCriterion, option.id),
      )
      .map((option) => option.id)
  },
  set(optionIds) {
    if (!screensizeCriterion) return

    const values = screensizeCriterion.options
      .filter((option) => optionIds.includes(option.id))
      .flatMap((option) => option.values)

    finder.setCriterionValues(screensizeCriterion, values)
  },
})

function handleSelectScreensizeRange([min, max]: [number, number]) {
  if (!screensizeCriterion) return

  if (
    min === screensizeCriterion.rangeMin &&
    max === screensizeCriterion.rangeMax
  ) {
    finder.setCriterionValues(screensizeCriterion, [])

    return
  }

  const inRangeValues = screensizeCriterion.options
    .filter((option) => {
      const numericId = Number.parseInt(option.id, 10)

      return Number.isFinite(numericId) && numericId >= min && numericId <= max
    })
    .flatMap((option) => option.values)

  finder.setCriterionValues(screensizeCriterion, inRangeValues)
}

const selectedUsagesIds = computed<string[]>({
  get() {
    if (!usagesCriterion) return []

    return usagesCriterion.options
      .filter((option) => finder.isOptionSelected(usagesCriterion, option.id))
      .map((option) => option.id)
  },
  set(optionIds) {
    if (!usagesCriterion) return

    const values = usagesCriterion.options
      .filter((option) => optionIds.includes(option.id))
      .flatMap((option) => option.values)

    finder.setCriterionValues(usagesCriterion, values)
  },
})

const selectedConditionThreshold = computed<string | undefined>({
  get() {
    if (!conditionsCriterion) return undefined

    const threshold = conditionsCriterion.options.find((option) =>
      finder.isOptionSelected(conditionsCriterion, option.id),
    )

    return threshold?.id
  },
  set(optionId) {
    if (!conditionsCriterion) return

    if (
      optionId === undefined ||
      selectedConditionThreshold.value === optionId
    ) {
      finder.setCriterionValues(conditionsCriterion, [])

      return
    }

    const clickedIndex = conditionsCriterion.options.findIndex(
      (option) => option.id === optionId,
    )
    if (clickedIndex === -1) return

    const expandedValues = conditionsCriterion.options
      .slice(clickedIndex)
      .flatMap((option) => option.values)

    finder.setCriterionValues(conditionsCriterion, expandedValues)
  },
})

const activeFiltersCount = computed(
  () => Object.values(filters.value).flat().length + (price.value ? 1 : 0),
)
</script>
